import { action, get } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Group } from "@shared/models/group"
import { ALL_SCHOOL_YEAR_IDS } from "@shared/models/school-year"
import { handleError } from "cc-frontend/error-handling"
import FindDoc from "cc-frontend/helpers/find-doc"
import groupChangeTitleAction from "cc-frontend/lib/actions/group/GROUP_CHANGE_TITLE"
import type Dialog from "cc-frontend/services/dialog"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Rpc from "cc-frontend/services/rpc"
import { isGroupMembershipGroup, isGroupMembershipIndividual } from "cc-frontend/utils/group"
import { easeIn, easeOut } from "ember-animated/easings/cosine"
import { fadeIn, fadeOut } from "ember-animated/motions/opacity"
import { use } from "ember-could-get-used-to-this"
import { filter, isNil } from "lodash-es"
import * as R from "remeda" // tree-shaking supported!
import { tracked } from "tracked-built-ins"
import { ACTIONS_ALL_COLUMN, AVATAR_COLUMN, NAME_COLUMN } from "./members-table"

export interface CreateTeamSignature {
  Args: {
    model: {
      groupId: string
      parentGroup: Group
    }
    order: number
    closeModal: () => {}
  }
}

export default class CreateTeam extends Component<CreateTeamSignature> {
  @service declare dispatcher: Dispatcher
  @service declare modalManager: ModalManager
  @service declare rpc: Rpc
  @service declare dialog: Dialog

  @tracked isDeleting = false

  elementInserted = false
  schoolYearIds = ALL_SCHOOL_YEAR_IDS
  membersTableColumns = [AVATAR_COLUMN, NAME_COLUMN, ACTIONS_ALL_COLUMN]

  @use group = new FindDoc(() => ["group", this.args.model.groupId]);

  *bodyTransition({ insertedSprites, keptSprites, removedSprites }) {
    insertedSprites.forEach((sprite) => {
      fadeIn(sprite, { easing: easeOut, duration: 150 })
    })

    keptSprites.forEach(fadeIn)

    removedSprites.forEach((sprite) => {
      fadeOut(sprite, { easing: easeIn, duration: 150 })
    })
  }

  get teamIds() {
    return R.pipe(
      get(get(this.args.model.parentGroup, "attributes"), "memberships"),
      R.filter(isGroupMembershipGroup),
      R.map((membership) => membership.attributes.groupId)
    )
  }

  get individualMemberships() {
    if (isNil(this.group.content)) return []
    return filter(get(this.group.content.attributes, "memberships"), isGroupMembershipIndividual)
  }

  get hasTitle() {
    let content = this.group.content
    if (content === null) return false
    let title = get(get(content, "attributes"), "title")
    return title !== "" && title !== null && title !== undefined
  }

  @action setTitle(title: string) {
    let content = this.group.content
    if (content === null) return false
    return this.dispatcher.dispatchPreparedAction(
      groupChangeTitleAction({
        groupId: content.id,
        value: title,
        originalValue: content.attributes.title,
      })
    )
  }

  @action
  addMember(group: Group) {
    this.modalManager.openModal("group/add-members", {
      group: group,
      makeAdmin: false,
      initiallyOpened: true,
    })
  }

  @action
  deleteTeam(group: Group) {
    this.dialog
      .blank("dialogs/confirm-delete-team", {
        className: "dialog--create-annotation",
      })
      .then(() => {
        this.isDeleting = true
        this.rpc
          .group_delete_child_group({
            groupId: this.args.model.parentGroup.id,
            childGroupId: group.id,
          })
          .then(() => {
            this.args.closeModal()
          })
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
  }
}
